import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

/**
 * Alvo mínimo de toque. 44pt é o piso das Human Interface Guidelines da Apple e o
 * critério de "target size" do WCAG 2.2. A recipe `.btn-sm` do components.css tem 38px
 * de altura nominal — abaixo do mínimo —, por isso o piso é imposto aqui por min-h/min-w
 * em vez de depender da altura de cada tamanho.
 */
export const MIN_TOUCH_TARGET_PX = 44;

const touchTarget = `min-h-[${MIN_TOUCH_TARGET_PX}px]`;

export const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-pill border border-transparent font-sans font-semibold',
    'transition-all duration-2 ease-soberano active:scale-[0.97]',
    // O glow dourado acende no toque/foco — nunca em loop (regra do design system).
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-40 disabled:grayscale-[0.3]',
    touchTarget,
  ),
  {
    variants: {
      variant: {
        /**
         * OURO METÁLICO — EXCLUSIVO DO CTA PRIMÁRIO.
         *
         * Regra inviolável do design system: `--metal` só pode aparecer em CTA primário,
         * Selo Powerful, logo, conquistas e preço-destaque. NUNCA em texto corrido nem em
         * áreas grandes. Use no máximo um botão `metal` por tela — se há dois CTAs
         * disputando a atenção, nenhum é o primário.
         */
        metal: 'bg-metal text-[color:var(--on-metal)] shadow-[var(--glow)] hover:brightness-[1.06] hover:shadow-[var(--glow-strong)]',
        secondary: 'bg-transparent text-gold-text border-gold hover:bg-gold/[0.12]',
        ghost: 'bg-surface-2 text-ink-1 border-[color:var(--hairline-2)] hover:bg-surface-3',
        danger: 'bg-transparent text-danger border-danger hover:bg-danger/[0.12]',
      },
      size: {
        sm: 'h-[38px] px-4 text-[length:var(--t-sm)]',
        md: 'h-[46px] px-6 text-[length:var(--t-base)]',
        lg: 'h-[54px] px-7 text-[length:var(--t-md)]',
        icon: `w-[46px] ${touchTarget.replace('min-h', 'min-w')} h-[46px] rounded-full p-0`,
      },
      full: { true: 'w-full' },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, full, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size, full }), className)}
      {...props}
    />
  ),
);

Button.displayName = 'Button';
